import { AuthTokenRepository } from 'core/repositories/AuthTokenRepository';
import { TokenKeeper } from './api/TokenKeeper';
import { Scope } from 'core/models/scope';
import { PostAssertResponse } from './api/Authorization';
import { ApiCall } from './api/apiCall';

function encodeScope(scope: Scope) {
    return scope.map(permission => {
        const { resource: { projectId, environmentId }, operations } = permission;
        return `${projectId}:environment:${environmentId}=${operations.sort().join(',')}`;
    }).join(' ');
}

function decodeScope(str: string): Scope {
    return str.split(' ').map(token => {
        const result = token.match(/^(.*):environment:(.*)=(.*)/);
        if (!result) {
            throw `unexpected permission: ${token}`;
        }

        const [projectId, environmentId, operations] = result.slice(1, 4);
        return { resource: { projectId, environmentId }, operations: operations.split(',') };
    });
}

export class AuthTokenRepositoryImpl implements AuthTokenRepository {
    constructor(private tokenKeeper: TokenKeeper | undefined, private apiCall: ApiCall | undefined) {
    }

    async get(): Promise<string> {
        if (!this.tokenKeeper) {
            throw 'no auth token keeper enabled';
        }
        return (await this.tokenKeeper.get()).token;
    }

    addListener(callback: (accessToken: string, expiresAt: Date) => void): void {
        if (!this.tokenKeeper) {
            console.warn('no auth token keeper enabled');
            return;
        }
        this.tokenKeeper.addListener(callback);

        const current = this.tokenKeeper.getCurrentInfo();
        if (current) {
            callback(current.token, current.expiresAt);
        }
    }

    removeListener(callback: (accessToken: string, expiresAt: Date) => void): void {
        if (!this.tokenKeeper) {
            console.warn('no auth token keeper enabled');
            return;
        }
        this.tokenKeeper.removeListener(callback);
    }

    async assertScope(accessToken: string, scope: Scope): Promise<Scope> {
        if (!this.apiCall) {
            throw 'apiCall is undefined';
        }

        // https://tier4.github.io/WebAutoAuth/dist/#operation/TokenService_Assert
        const res = await this.apiCall<PostAssertResponse>('/v2/assert', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                token: accessToken,
                scope: encodeScope(scope)
            }),
        });
        return decodeScope(res.scope);
    }
}
